import React, {ReactElement, useEffect} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
    useGetPreparedCartQuery,
    useUpdatePreparedCartProductMutation
} from '../Api/PreparedCartEndpoints';
import { useLocation } from 'react-router-dom';
import {
    MessageBar,
    MessageBarBody,
    MessageBarActions,
    Button, DrawerBody, DrawerHeader, DrawerHeaderTitle, Drawer, DrawerFooter
} from '@fluentui/react-components';
import { ProductImage } from '../Components/Helpers/ProductImage';
import {
    DismissRegular,
    ArrowCircleUpFilled,
    ArrowCircleDownFilled,
    ShoppingBagDismissFilled,
    Dismiss24Regular,
    BookInformationFilled,
    NewFilled,
    BoxMultipleSearchFilled,
    EditRegular
} from "@fluentui/react-icons";
import EditBundleDrawer from '../Components/Cart/EditBundleDrawer';
import CurrencyFormat from '../Components/Helpers/CurrencyFormat';
import useStyles from '../styles';
import NumberInput from '../Components/Helpers/NumberInput';
import TruncateText from '../Components/Helpers/TruncateText';
import FullPageSpinner from '../Components/Spinner/FullPageSpinner';
import CartSummary from '../Components/Cart/CartSummary';
import {ICartBundle, ICartProduct} from "../Api/Models/Cart";
import CartNotification from '../Components/Cart/CartNotification';
import {clearSelectedCart, setSelectedCart, setDismissedInstructions} from '../Store/CartSlice';
import {useStartNewCart} from '../Components/Cart/useStartNewCart';
import {useDispatch, useSelector} from 'react-redux';
import MarkdownView from "react-showdown";
import {RootState} from "../Store/Store";
import {useLocalization} from '../Components/Helpers/useLocalization';


const PreparedCartPage: React.FC = () => {

    const { cartId } = useParams<{ cartId: string }>();
    const L = useLocalization();
    const settings = useSelector((state: RootState) => state.cart);
    const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
    const cart = useGetPreparedCartQuery({cartId: cartId ?? ""}, {skip: !cartId});
    const [updateCart] = useUpdatePreparedCartProductMutation();
    const [updateCartBundle] = useUpdatePreparedCartProductMutation();
    const [updating, setUpdating] = React.useState(false);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const error = queryParams.get('error');
    const styles = useStyles();
    const [products, setProducts] = React.useState<ICartProduct[]>([]);
    const [userAddedProduct, setUserAddedProducts] = React.useState<ICartProduct[]>([]);
    const [bundles, setBundles] = React.useState<ICartBundle[]>([]);
    const [userAddedBundles, setUserAddedBundles] = React.useState<ICartBundle[]>([]);
    const [errorProducts, setErrorProducts] = React.useState<string[]>([])
    const [errorBundles, setErrorBundles] = React.useState<string[]>([])
    const [dismissedItemNotificationList, setDismissedItemNotificationList] = React.useState<string[]>([]);
    const navigate = useNavigate();
    const [warning, setWarning] = React.useState<string | null>(null);
    const dispatch = useDispatch();
    const { startNewCart, canStartNewCart } = useStartNewCart();
    const [showSlideout, setShowSlideout] = React.useState(false);
    // The bundle whose configuration is being edited in the slide-out panel (null = closed).
    // editingBundleLocked carries the rep's pre-done product ids (undefined = full edit).
    const [editingBundle, setEditingBundle] = React.useState<ICartBundle | null>(null);
    const [editingBundleLocked, setEditingBundleLocked] = React.useState<string[] | undefined>(undefined);

    useEffect(() => {
        if(cart.data?.instructions && !settings.dismissedInstructions) {
            setShowSlideout(true)
        }
    }, [cart.data?.instructions, settings.dismissedInstructions]);
    
    React.useEffect(() => {
        // Only stash the cart ID once we've confirmed we got valid data back
        if (cartId && cart.data && !cart.isError) {
            dispatch(setSelectedCart(cartId));
        }
    }, [cartId, cart.data, cart.isError, dispatch])
    
    React.useEffect(() => {
        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => {
            window.removeEventListener('resize', handleResize);
            setShowSlideout(false)
        };
    }, []);
    React.useEffect(() => {
        if(cart.isError || (!cart.isLoading && !cart.isFetching && !cart.data)){
            // The cart in the URL is invalid — check if we have a different valid cart in localStorage
            const storedCartId = localStorage?.getItem('selectedCartId');
            if (storedCartId && storedCartId !== cartId) {
                // A different cart is stashed — navigate to it; validity will be confirmed when that page loads
                navigate(`/prepared-cart/${storedCartId}`);
            } else {
                // Nothing valid to fall back to — clear and go to the regular cart
                dispatch(clearSelectedCart());
                navigate('/cart');
            }
        }
        else if(cart.data?.products) {
            const userAdded = cart.data.products.filter(bundle => bundle.userAdded);
            setUserAddedProducts(userAdded);
            const notUserAdded = cart.data.products.filter(bundle => bundle.userAdded !== true);
            setProducts(notUserAdded);
        }
    }, [cart.data, cart.isFetching, cart.isLoading, cart.isError, dispatch, navigate])
    
    React.useEffect(() => {
        if(cart.data?.bundles) {
            const userAdded = cart.data.bundles.filter(bundle => bundle.userAdded);
            setUserAddedBundles(userAdded);
            const notUserAdded = cart.data.bundles.filter(bundle => bundle.userAdded !== true);
            setBundles(notUserAdded);
        }
    }, [cart.data?.bundles])
    

    const updateQuantity = async (workspaceItemId: string, productId: string, quantity: number) => {
        setErrorProducts([])
        
        if(quantity < 0){
            return;
        }
        
        const totalOtherItemQuantity = cart.data?.products.filter(p => p.productId === productId && p.workspaceItemId !== workspaceItemId)?.map(a => a.quantity).reduce((a, b) => a + b, 0) ?? 0;
        
        const relatedProduct = cart.data?.products.find(p => p.workspaceItemId === workspaceItemId);
        if(!relatedProduct || relatedProduct?.productId === undefined) return;
        if (relatedProduct.maxQuantity && (quantity + totalOtherItemQuantity) > relatedProduct.maxQuantity) {
            setErrorProducts((existingErrorProducts) => [...existingErrorProducts, relatedProduct.productId]);
            
            if(cartId && relatedProduct?.workspaceItemId) {
                setUpdating(true);
                await updateCart({
                    cartId,
                    wsItemId: relatedProduct.workspaceItemId ?? '',
                    quantity: (relatedProduct.maxQuantity - totalOtherItemQuantity)
                });
                setUpdating(false);
            }
        }
        else if(cartId && relatedProduct?.workspaceItemId) {
            setProducts((previousProducts) =>
                previousProducts.map(product =>
                    product.workspaceItemId === workspaceItemId ? {...product, quantity: quantity} : product
                )
            );
            setUpdating(true);
            await updateCart({cartId, wsItemId: relatedProduct.workspaceItemId, quantity});
            setUpdating(false);
        }
    }
    
    
    const updateBundleQuantity = async (bundleId: string, workspaceItemId: string, quantity: number) => {
        setErrorBundles([])
        if(quantity < 0){
            return;
        }
        const totalOtherItemQuantity = cart.data?.bundles.filter(p => p.bundleId === bundleId && p.workspaceItemId !== workspaceItemId)?.map(a => a.quantity).reduce((a, b) => a + b, 0) ?? 0;

        const relatedBundle = cart.data?.bundles?.find(p => p.bundleId === bundleId);
        if(!relatedBundle || relatedBundle?.bundleId === undefined) return;
        
        if(relatedBundle.maxQuantity && (quantity + totalOtherItemQuantity) > relatedBundle.maxQuantity) {
            setErrorBundles((existingErrorBundles) => [...existingErrorBundles, relatedBundle.bundleId]);
            if(cartId && relatedBundle?.workspaceItemId) {
                setUpdating(true);
                await updateCartBundle({
                    cartId,
                    wsItemId: relatedBundle.workspaceItemId ?? '',
                    quantity: (relatedBundle.maxQuantity - totalOtherItemQuantity)
                });
                setUpdating(false);
            }
        }
        else if(cartId && relatedBundle.workspaceItemId) {
            setUpdating(true);
            setBundles((previousBundles) => 
                previousBundles.map(bundle =>
                    bundle.bundleId === bundleId? {...bundle, quantity: quantity} : bundle
                )
            );
            await updateCartBundle({cartId, wsItemId: relatedBundle.workspaceItemId, quantity})
                .unwrap()
                .then((response) => {
                    if(response.message) {
                        setWarning(response.message);
                    }
                })
            setUpdating(false);
        }
    }
    

    const handleResize = () => {
        setWindowWidth(window.innerWidth);
    };
    
    const shouldShowProductChangeNotification = (product: ICartProduct) => {
        return cart.data?.alternativeProducts && cart.data.alternativeProducts.find((prod) => prod.savedSearchId !== '' && prod.savedSearchId === product.savedSearchId) && !dismissedItemNotificationList.find(a => a === product.productId);
    }
    const onNewCart = async () => {
        await startNewCart();
        navigate('/home');
    }


    if (cart.isLoading) return <FullPageSpinner message={L('localization_common_loading')} />
    if (cart.error) return <div>{L('localization_prepared_load_error')}</div>
    if (cart.data?.expired) {
        return (
            <div>
                <div className={'centered-content'}>
                    <ShoppingBagDismissFilled style={{fontSize: '24em', color: '#ddd', paddingBottom: '20px'}} />
                    <p><h1>{L('localization_prepared_expired_heading')}</h1></p>
                    <p>{L('localization_prepared_expired_contact')}</p>
                    {canStartNewCart &&
                    <Button
                        appearance='primary'
                        onClick={onNewCart}
                        icon={<NewFilled/>}
                        style={{width: '25%', marginTop: '1em', maxWidth:'200px'}}
                    >
                        {L('localization_cart_new_cart_button')}
                    </Button>}
                </div>
            </div>)
    }
    
    
    type CartItem = { type: 'product'; item: ICartProduct } | { type: 'bundle'; item: ICartBundle };

    const groupAndSortItems = (products: ICartProduct[], bundles: ICartBundle[]): Map<string, CartItem[]> => {
        const allItems: CartItem[] = [
            ...products.map(p => ({ type: 'product' as const, item: p })),
            ...bundles.map(b => ({ type: 'bundle' as const, item: b })),
        ];
        allItems.sort((a, b) => (a.item.sortOrder ?? 0) - (b.item.sortOrder ?? 0));
        const groups = new Map<string, CartItem[]>();
        for (const entry of allItems) {
            const key = entry.item.groupName ?? '';
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key)!.push(entry);
        }
        return groups;
    };

    const getNormalScreenedProducts = (products: ICartProduct[], bundles: ICartBundle[], header?: React.ReactElement) => {
        const groups = groupAndSortItems(products, bundles);
        return (
            <div>
                {header}
                {Array.from(groups.entries()).map(([groupName, items]) => (
                    <div  key={groupName || '__ungrouped__'}>
                        {groupName && <h3 style={{margin: '1em 0 0.25em'}}>{groupName}</h3>}
                        <div className={'cart-section'} style={{backgroundColor: 'white', padding: '1em'}}>
                            <table className='checkout-table'>
                            <thead>
                            <tr>
                                <th style={{width: '100px'}}/>
                                <th>{L('localization_cart_col_product')}</th>
                                <th style={{width: '150px'}}>{L('localization_cart_col_quantity')}</th>
                                <th style={{width: '100px', textAlign: 'center'}}>{L('localization_cart_col_price')}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {items.map((entry) => {
                                if (entry.type === 'product') {
                                    const product = entry.item;
                                    return (
                                        <React.Fragment key={`product-${product.workspaceItemId ?? product.productId}`}>
                                            {(shouldShowProductChangeNotification(product) || (product.lastPrice != null && product.price != null && product.price !== product.lastPrice && !dismissedItemNotificationList.find(a => a === product.productId))) && (
                                            <tr className='checkout-price-change'>
                                                {shouldShowProductChangeNotification(product) &&
                                                    <td colSpan={4} style={{padding: 0}}>
                                                        <div style={{width: '100%', display: 'flex', justifyContent: 'center'}}>
                                                            <MessageBar style={{width: '100%'}}>
                                                                <MessageBarBody>
                                                                    <div style={{display: 'flex'}}>{L('localization_cart_product_replaced_row')}</div>
                                                                </MessageBarBody>
                                                                <MessageBarActions containerAction={
                                                                    <Button aria-label="dismiss" appearance="transparent" icon={<DismissRegular/>}
                                                                        onClick={() => setDismissedItemNotificationList((prev) => [...prev, product.productId])}/>
                                                                }/>
                                                            </MessageBar>
                                                        </div>
                                                    </td>}
                                                {product.lastPrice != null && product.price != null && product.price !== product.lastPrice && !dismissedItemNotificationList.find(a => a === product.productId) &&
                                                    <td colSpan={4} style={{padding: 0}}>
                                                        <div style={{width: '100%', display: 'flex', justifyContent: 'center'}}>
                                                            <MessageBar style={{width: '100%'}} icon={''}>
                                                                <MessageBarBody>
                                                                    <div style={{display: 'flex'}}>
                                                                        {product.price > product.lastPrice ?
                                                                            <div style={{display: 'flex', justifyContent: 'center', alignContent: 'center'}}>
                                                                                <div style={{color: 'red', paddingRight: '5px'}}><ArrowCircleUpFilled style={{paddingRight: '5px', fontSize: '32px'}}/></div>
                                                                                <div style={{fontWeight: 'bold', paddingRight: '5px', alignContent: 'center'}}>{L('localization_cart_price_increased')}</div>
                                                                            </div> :
                                                                            <div style={{display: 'flex', justifyContent: 'center', alignContent: 'center'}}>
                                                                                <div style={{color: 'green', paddingRight: '5px'}}><ArrowCircleDownFilled style={{paddingRight: '5px', fontSize: '32px'}}/></div>
                                                                                <div style={{fontWeight: 'bold', paddingRight: '5px', alignContent: 'center'}}>{L('localization_cart_price_drop')}</div>
                                                                            </div>}
                                                                        <div style={{justifyContent: 'center', alignContent: 'center'}}>{L('localization_cart_price_changed_from')} <b><CurrencyFormat value={product.lastPrice * product.quantity}/></b> {L('localization_cart_price_changed_to')} <b><CurrencyFormat value={product.price * product.quantity}/></b></div>
                                                                    </div>
                                                                </MessageBarBody>
                                                                <MessageBarActions containerAction={
                                                                    <Button aria-label="dismiss" appearance="transparent" icon={<DismissRegular/>}
                                                                        onClick={() => setDismissedItemNotificationList((prev) => [...prev, product.productId])}/>
                                                                }/>
                                                            </MessageBar>
                                                        </div>
                                                    </td>}
                                            </tr>
                                            )}
                                            <tr className='checkout-product-row'>
                                                <td style={{width: '100px'}}>
                                                    {product.image && <ProductImage style={{width: '100px', height: '100px'}} src={product.image} fit='contain'/>}
                                                </td>
                                                <td><h4>{product.name}</h4></td>
                                                <td>
                                                    {(product.userAdded !== true && !cart.data?.canModifyItemQuantities)
                                                        ? <div style={{paddingLeft: '10px'}}><h3>{product.quantity}</h3></div>
                                                        : <NumberInput value={product.quantity} onChange={async (v) => { await updateQuantity(product.workspaceItemId ?? '', product.productId, v) }} style={{width: '100%'}} includeButtons disabled={updating || cart.isFetching}/>
                                                    }
                                                    {errorProducts.find(a => a === product.productId) &&
                                                        <MessageBar intent="warning"><MessageBarBody>{`${L('localization_cart_limited_to')} ${product.maxQuantity}`}</MessageBarBody></MessageBar>}
                                                </td>
                                                <td style={{textAlign: 'right'}}>
                                                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                                                        {product.originalPrice && product.price && product.price < product.originalPrice && <CurrencyFormat value={product.originalPrice * product.quantity} strikeThrough={true}/>}
                                                        <h3>{product.price && <CurrencyFormat value={product.price * product.quantity}/>}</h3>
                                                    </div>
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    );
                                } else {
                                    const bundle = entry.item;
                                    const canEditBundle = bundle.userAdded === true
                                        ? cart.data?.canAddAdditionalItems === true
                                        : cart.data?.allowBundleConfiguration === true;
                                    return (
                                        <tr key={`bundle-${bundle.workspaceItemId ?? bundle.bundleId}`} className='checkout-bundle-row'>
                                            <td style={{width: '100px'}}>
                                                {bundle.image && <ProductImage style={{width: '100px', height: '100px'}} src={bundle.image} fit='contain'/>}
                                            </td>
                                            <td>
                                                <div>
                                                    <div style={{display: 'flex', alignItems: 'center', gap: '0.5em'}}>
                                                        <h4>{bundle.name}</h4>
                                                        {canEditBundle && (
                                                            <Button
                                                                appearance='subtle'
                                                                size='small'
                                                                aria-label={L('localization_cart_edit_bundle')}
                                                                icon={<EditRegular />}
                                                                onClick={() => {
                                                                    setEditingBundleLocked(bundle.userAdded === true
                                                                        ? undefined
                                                                        : bundle.preparedConfiguration?.map(s => s.productId));
                                                                    setEditingBundle(bundle);
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                    {/*this is a cart bundle so the only products on it should be the selected ones*/}
                                                    {bundle.products?.map((product) => (
                                                        <p key={product.productId} className='cart-bundle-subproduct'>{product.name} x {product.quantity}</p>
                                                    ))}
                                                </div>
                                            </td>
                                            <td>
                                                {(bundle.userAdded !== true && !cart.data?.canModifyItemQuantities)
                                                    ? <div style={{paddingLeft: '10px'}}><h3>{bundle.quantity}</h3></div>
                                                    : <NumberInput value={bundle.quantity} onChange={(v) => updateBundleQuantity(bundle.bundleId, bundle.workspaceItemId ?? '', v)} style={{width: '100%'}} includeButtons disabled={updating || cart.isFetching}/>}
                                                {errorBundles.find(a => a === bundle.bundleId) &&
                                                    <MessageBar intent="warning"><MessageBarBody>{`${L('localization_cart_limited_to')} ${bundle.maxQuantity}`}</MessageBarBody></MessageBar>}
                                            </td>
                                            <td style={{textAlign: 'right'}}>
                                                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                                                    {bundle.originalPrice && bundle.price && bundle.price < bundle.originalPrice && <CurrencyFormat value={bundle.originalPrice * bundle.quantity} strikeThrough={true}/>}
                                                    <h3>{bundle.price && <CurrencyFormat value={bundle.price * bundle.quantity}/>}</h3>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }
                            })}
                            </tbody>
                        </table>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    const getMobileScreenedProducts = (products: ICartProduct[], bundles: ICartBundle[], header?: React.ReactElement) => {
        const groups = groupAndSortItems(products, bundles);
        return (
            <div>
                {header}
                {Array.from(groups.entries()).map(([groupName, items]) => (
                    <div key={groupName || '__ungrouped__'}>
                        {groupName && <h3 style={{margin: '1em 0 0.25em'}}>{groupName}</h3>}
                        <table className='checkout-table checkout-table-mobile'>
                            <tbody>
                            {items.map((entry) => {
                                if (entry.type === 'product') {
                                    const product = entry.item;
                                    return (
                                        <tr key={product.userAdded ? `${product.productId}-usercheckoutproduct` : `${product.productId}-checkoutproduct`} className='checkout-product-row'>
                                            <td style={{width: '75px'}}>
                                                {product.image && <ProductImage style={{width: '75px', height: '75px'}} src={product.image} fit='contain'/>}
                                            </td>
                                            <td>
                                                <h4>{product.name && <TruncateText text={product.name} maxLength={50}/>}</h4>
                                            </td>
                                            <td style={{textAlign: 'right'}}>
                                                {(product.userAdded !== true && !cart.data?.canModifyItemQuantities)
                                                    ? <div style={{paddingLeft: '10px'}}><h3>{product.quantity}</h3></div>
                                                    : <NumberInput value={product.quantity} onChange={(v) => updateQuantity(product.workspaceItemId ?? '', product.productId, v)} style={{width: '100%', marginBottom: '1em', fontSize: '16px'}} includeButtons disabled={updating || cart.isFetching}/>
                                                }
                                                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                                                    {product.originalPrice && product.price && product.price < product.originalPrice && <CurrencyFormat value={product.originalPrice * product.quantity} strikeThrough={true}/>}
                                                    <h3>{product.price && <CurrencyFormat value={product.price * product.quantity}/>}</h3>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                } else {
                                    const bundle = entry.item;
                                    const canEditBundleMobile = bundle.userAdded === true
                                        ? cart.data?.canAddAdditionalItems === true
                                        : cart.data?.allowBundleConfiguration === true;
                                    return (
                                        <tr key={`${bundle.workspaceItemId ?? bundle.bundleId}-${bundle.userAdded ? 'usercheckoutbundle' : 'checkoutbundle'}`} className='checkout-bundle-row'>
                                            <td style={{width: '75px'}}>
                                                {bundle.image && <ProductImage style={{width: '75px', height: '75px'}} src={bundle.image} fit='contain'/>}
                                            </td>
                                            <td>
                                                <div>
                                                    <div style={{display: 'flex', alignItems: 'center', gap: '0.5em'}}>
                                                        <h4>{bundle.name && <TruncateText text={bundle.name} maxLength={50}/>}</h4>
                                                        {canEditBundleMobile && (
                                                            <Button
                                                                appearance='subtle'
                                                                size='small'
                                                                aria-label={L('localization_cart_edit_bundle')}
                                                                icon={<EditRegular />}
                                                                onClick={() => {
                                                                    setEditingBundleLocked(bundle.userAdded === true
                                                                        ? undefined
                                                                        : bundle.preparedConfiguration?.map(s => s.productId));
                                                                    setEditingBundle(bundle);
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                    {/*this is a cart bundle so the only products on it should be the selected ones*/}
                                                    {bundle.products?.map((product) => (
                                                        <p key={product.productId}>{product.name} x {product.quantity}</p>
                                                    ))}
                                                </div>
                                            </td>
                                            <td style={{textAlign: 'right'}}>
                                                {(bundle.userAdded !== true && !cart.data?.canModifyItemQuantities)
                                                    ? <div style={{paddingLeft: '10px'}}><h3>{bundle.quantity}</h3></div>
                                                    : <NumberInput value={bundle.quantity} onChange={(v) => updateBundleQuantity(bundle.bundleId, bundle.workspaceItemId ?? '', v)} style={{width: '100%', marginBottom: '1em', fontSize: '16px'}} includeButtons disabled={updating || cart.isFetching}/>}
                                                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                                                    {bundle.originalPrice && bundle.price && bundle.price < bundle.originalPrice && <CurrencyFormat value={bundle.originalPrice * bundle.quantity} strikeThrough={true}/>}
                                                    <h3>{bundle.price && <CurrencyFormat value={bundle.price * bundle.quantity}/>}</h3>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }
                            })}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>
        );
    }

    const getProductsTable = (products: ICartProduct[], bundles: ICartBundle[], header?: ReactElement) => {
        if (windowWidth > 700) {
            return getNormalScreenedProducts(products, bundles, header);
        } else {
            return getMobileScreenedProducts(products, bundles, header);
        }
    }

    return (
        <div className={styles.mainContent}>
            <div style={{display: 'flex', flexDirection: 'column'}}>
                
                <div>
                    {warning ?
                    <CartNotification 
                        onDismiss={() => {setWarning(null)}}
                        message={warning}
                    /> : <></>}
    
                    {(products.length === 0 && bundles.length === 0 && userAddedBundles.length === 0 && userAddedProduct.length === 0) ? 
                    <div>
                        <div className={'centered-content'}>
                            <BoxMultipleSearchFilled style={{fontSize: '24em', color: '#ddd', paddingBottom: '20px'}} />
                            <h1>{L('localization_prepared_empty_heading')}</h1>
                            {canStartNewCart &&
                            <Button
                                icon={<NewFilled/>}
                                style={{width: '25%', marginTop: '1em', maxWidth:'200px'}}
                                appearance='primary'
                                onClick={onNewCart}
                            >
                                {L('localization_cart_new_cart_button')}
                            </Button>}
                        </div>
                    </div> : 
                    <div>
                        <div style={{display: 'flex', flexDirection: windowWidth > 900 ? 'row' : 'column', alignItems: 'flex-start', gap: '2rem'}}>
                            <div style={{flex: 2, minWidth: 0}}>
                                <div className={'product-table-container'}>
                                    <h1>{L('localization_prepared_products_heading')}</h1>
                                    <div className={'prepared-cart-description'}>
                                        {cart.data?.description &&
                                            <MarkdownView markdown={cart.data.description}/>
                                        }
                                    </div>
                                    {getProductsTable(products, bundles)}
                                </div>
                                {userAddedProduct.length > 0 &&
                                <div className={'product-table-container'}>
                                    {getProductsTable(userAddedProduct, [], <h2>{L('localization_prepared_added_products')}</h2>)}
                                </div>}
                                {userAddedBundles.length > 0 &&
                                <div className={'product-table-container'}>
                                    {getProductsTable([], userAddedBundles, <h2>{L('localization_prepared_added_bundles')}</h2>)}
                                </div>}
                            </div>
                            {!showSlideout && cart.data?.instructions &&
                                <Button style={{marginTop: '10px'}} aria-label={"Show Instructions"} title={"Show Instructions"} icon={<BookInformationFilled/>} onClick={() => setShowSlideout((current) => !current)}/>
                            }
                            <div className='prepared-cart-summary' style={{width: 320, flexShrink: 0, position: 'sticky', top: '1em'}}>
                                <CartSummary
                                    error={error ?? undefined}
                                    cart={cart.data}
                                    preparedCartId={cartId}
                                />
                                <Drawer
                                    type={'inline'}
                                    size={"medium"}
                                    style={{width:'100%'}}
                                    separator
                                    open={showSlideout}
                                    onOpenChange={(_, { open }) => {
                                        setShowSlideout(open);
                                    }}
                                    className='order-info-panel'
                                >
                                    <DrawerHeader className='panel-header'>
                                        <DrawerHeaderTitle
                                            className='panel-title'
                                            action={
                                                <Button
                                                    appearance="subtle"
                                                    aria-label="Close"
                                                    icon={<Dismiss24Regular/>}
                                                    onClick={() => {
                                                        setShowSlideout(false)
                                                        dispatch(setDismissedInstructions(true))
                                                    }}

                                                />
                                            }
                                        >
                                            {L('localization_prepared_instructions_title')}

                                        </DrawerHeaderTitle>
                                    </DrawerHeader>

                                    <DrawerBody className='panel-body'>
                                        <MarkdownView markdown={cart.data?.instructions ?? ""}/>
                                    </DrawerBody>
                                    <DrawerFooter>
                                        <div style={{marginTop: '1em'}}>
                                            <div style={{paddingTop: '1em'}}>
                                                <Button appearance='primary' style={{width: '100%'}}
                                                        onClick={() => setShowSlideout(false)}>{L('localization_common_close')}</Button>
                                            </div>
                                        </div>
                                    </DrawerFooter>
                                </Drawer>
                            </div>
                        </div>
                    </div>
                    }
                </div>
            </div>
            {/* Bundle configuration drawer for prepared-cart bundles. Uses workspaceItemId as the
                save target and passes lockedProductIds for rep-configured bundles. */}
            <EditBundleDrawer
                cartBundle={editingBundle}
                open={!!editingBundle}
                onOpenChange={(open) => { if (!open) { setEditingBundle(null); setEditingBundleLocked(undefined); } }}
                mode='prepared'
                preparedCartId={cartId}
                workspaceItemId={editingBundle?.workspaceItemId}
                lockedProductIds={editingBundleLocked}
            />
        </div>
    )
}

export default PreparedCartPage;

